import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { updateRemoteCardPinCode } from '../../../assets/services/cards';
import {
  CARD_PIN_LENGTH,
  CARD_PIN_VALIDATION_ERRORS,
  getCardPinValidationError,
} from '../../../assets/utils/cardPin';
import PinDots from '../../PinPages/PinDots/PinDots';
import PinPage from '../../PinPages/PinPage/PinPage';
import PinPad from '../../PinPages/PinPad/PinPad';
import { usePinEntry } from '../../PinPages/usePinEntry/usePinEntry';
import './CardPinChange.css';

const STEP = Object.freeze({
  NEW: 'new',
  CONFIRM: 'confirm',
});
const COPY_TRANSITION = {
  duration: 0.16,
  ease: [0.22, 1, 0.36, 1],
};
const PROCESSING_MS = 700;
const SUCCESS_FEEDBACK_MS = 600;
const ERROR_PAUSE_MS = 360;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getValidationMessage(error, cardCopy) {
  if (error === CARD_PIN_VALIDATION_ERRORS.REPEATED_DIGITS) {
    return cardCopy.pinRepeatedDigits;
  }

  if (error === CARD_PIN_VALIDATION_ERRORS.CONSECUTIVE_DIGITS) {
    return cardCopy.pinConsecutiveDigits;
  }

  return cardCopy.pinFormatError;
}

export function CardPinChange({ updatePinCode = updateRemoteCardPinCode }) {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const { card: cardCopy } = useContext(TranslationContext);
  const isMountedRef = useRef(true);
  const [step, setStep] = useState(STEP.NEW);
  const [newPin, setNewPin] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorPulse, setErrorPulse] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const showError = useCallback((message) => {
    setHasError(true);
    setErrorPulse((value) => value + 1);
    toast.error(message);
  }, []);

  const handleComplete = useCallback(async (value) => {
    setIsChecking(true);

    if (step === STEP.NEW) {
      const validationError = getCardPinValidationError(value);

      if (validationError) {
        await delay(ERROR_PAUSE_MS);

        if (isMountedRef.current) {
          showError(getValidationMessage(validationError, cardCopy));
          setIsChecking(false);
        }
        return false;
      }

      setNewPin(value);
      setHasError(false);
      setIsChecking(false);
      setStep(STEP.CONFIRM);
      return false;
    }

    if (value !== newPin) {
      await delay(ERROR_PAUSE_MS);

      if (isMountedRef.current) {
        showError(cardCopy.pinMismatch);
        setIsChecking(false);
      }
      return false;
    }

    setIsProcessing(true);
    const minProcessingDelay = delay(PROCESSING_MS);

    try {
      await updatePinCode(cardId, newPin);
      await minProcessingDelay;

      if (!isMountedRef.current) {
        return true;
      }

      setIsProcessing(false);
      setIsSuccess(true);
      await delay(SUCCESS_FEEDBACK_MS);

      if (!isMountedRef.current) {
        return true;
      }

      toast.success(cardCopy.pinChanged);
      navigate(`/cards/${encodeURIComponent(cardId)}`, { replace: true });
      return true;
    } catch (error) {
      await minProcessingDelay;

      if (!isMountedRef.current) {
        return false;
      }

      showError(error?.message || cardCopy.pinUpdateFailed);
      setIsChecking(false);
      setIsProcessing(false);
      return false;
    }
  }, [cardCopy, cardId, navigate, newPin, showError, step, updatePinCode]);

  const { digits, append, remove, reset } = usePinEntry(CARD_PIN_LENGTH, handleComplete);

  useEffect(() => {
    reset();
    setHasError(false);
    setIsChecking(false);
    setIsProcessing(false);
    setIsSuccess(false);
  }, [reset, step]);

  const handleDigit = useCallback((digit) => {
    if (hasError) {
      setHasError(false);
    }

    append(digit);
  }, [append, hasError]);

  const handleBack = () => {
    if (isChecking || isProcessing) return;

    if (step === STEP.CONFIRM) {
      setNewPin('');
      setStep(STEP.NEW);
      return;
    }

    navigate(`/cards/${encodeURIComponent(cardId)}`);
  };

  const copy = step === STEP.NEW
    ? {
      title: cardCopy.newPinTitle,
      subtitle: cardCopy.newPinSubtitle,
    }
    : {
      title: cardCopy.confirmPinTitle,
      subtitle: cardCopy.confirmPinSubtitle,
    };

  return (
    <PinPage className="card-pin-change">
      <div className="pin-page__body card-pin-change__body">
        <div className="pin-page__intro card-pin-change__intro">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={COPY_TRANSITION}
          >
            <h1 className="pin-page__title">{copy.title}</h1>
            <p className="pin-page__subtitle">{copy.subtitle}</p>
          </motion.div>
        </div>

        <PinDots
          length={CARD_PIN_LENGTH}
          filledCount={digits.length}
          hasError={hasError}
          errorKey={errorPulse}
          isProcessing={isProcessing && digits.length === CARD_PIN_LENGTH && !hasError}
          isSuccess={isSuccess && digits.length === CARD_PIN_LENGTH && !hasError}
          className="pin-dots--with-feedback"
        />

        <div className="card-pin-change__pad-group">
          <PinPad
            onDigit={handleDigit}
            onDelete={remove}
            onBack={handleBack}
            showDelete={digits.length > 0}
            disabled={isChecking || isProcessing}
            disabledDigits={digits}
          />
        </div>
      </div>
    </PinPage>
  );
}

export default CardPinChange;
